import React, { type ChangeEventHandler } from 'react';

import type {
	StripeCardCvcElementChangeEvent,
	StripeCardExpiryElementChangeEvent,
	StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { InfoTooltip } from 'src/app/shared/components/tooltips';
import type { StripeValidationErrors } from 'src/app/shared/utils/payments';

import { CardCvcElementWrapper } from './StripeElementWrappers/CardCvcElementWrapper';
import { CardExpiryElementWrapper } from './StripeElementWrappers/CardExpiryElementWrapper';
import { CardNumberElementWrapper } from './StripeElementWrappers/CardNumberElementWrapper';
import { Input } from './StripeElementWrappers/Input';

const sunriseInputStyles = {
	borderColor: 'sunrise.semantic.stroke.weaker',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&[data-error=true]': {
		borderColor: 'sunrise.semantic.stroke.error.weak',
		borderWidth: 1,
		boxShadow: 'none',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:hover': {
		borderColor: 'sunrise.semantic.stroke.accent.primary.strong',
		boxShadow: 'none',
		cursor: 'pointer',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:hover::placeholder': {
		color: 'sunrise.semantic.typography.strong',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:has(.StripeElement--focus):not(:has(.StripeElement--invalid))': {
		borderColor: 'sunrise.semantic.stroke.accent.primary.strong',
		borderWidth: 2,
		boxShadow: 'none',
	},
};

type Props = {
	validationErrors?: StripeValidationErrors;
	onChange?: (
		e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
	) => void;
	onCardNumberFieldFocus: () => void;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	isSunriseEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export function StripeCreditCardElements({
	validationErrors,
	onChange,
	onCardNumberFieldFocus,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	isSunriseEnabled,
}: Props): React.JSX.Element {
	const { translate } = useI18n();

	return (
		<>
			<Box
				mb={8}
				data-testid="credit-card-number"
				sx={{
					position: 'relative',
					WebkitTransform: 'translate3d(0, 0, 0)',
				}}
			>
				<Box sx={{ position: 'absolute', right: '0', top: '0' }}>
					<Image
						alt={translate('corgi.payments.stripe_elements.card_image_alt_text')}
						height="20px"
						src="https://assets.change.org/app-assets/victory_fund/credit-card-icons-2.png"
						width="163px"
						tabIndex={0}
						aria-description="Visa, Mastercard, American Express, Discover, JCB"
					/>
				</Box>
				<Input
					className="iframe-form-element" // TODO: this is here for the regression-qaa tests. Migrate the tests to use the data-qa attribute.
					data-qa="iframe-form-element"
					error={validationErrors?.card_number}
					label={translate('fe.components.credit_card.cc_number')}
					name="card-number"
					as={CardNumberElementWrapper}
					onFocus={onCardNumberFieldFocus}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					onChange={onChange as unknown as ChangeEventHandler<HTMLInputElement>}
					id="card-number-input"
					isSunriseEnabled={isSunriseEnabled}
					sx={{
						height: 40,
						display: 'flex',
						alignItems: 'center',
						// eslint-disable-next-line @typescript-eslint/naming-convention
						'.StripeElement': {
							width: '100%',
						},
						...(isSunriseEnabled && sunriseInputStyles),
					}}
				/>
			</Box>

			<Flex sx={{ width: '100%' }}>
				<Box sx={{ flex: 1 }} mr={8} data-testid="credit-card-expiry">
					<Input
						className="iframe-form-element" // TODO: this is here for the regression-qaa tests. Migrate the tests to use the data-qa attribute.
						data-qa="iframe-form-element"
						error={validationErrors?.card_expiration}
						label={translate('corgi.payments.stripe_elements.expiration')}
						name="card-expiration"
						as={CardExpiryElementWrapper}
						onFocus={onCardExpiryFieldFocus}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						onChange={onChange as unknown as ChangeEventHandler<HTMLInputElement>}
						id="card-expiry-input"
						isSunriseEnabled={isSunriseEnabled}
						sx={{
							height: 40,
							display: 'flex',
							alignItems: 'center',
							// eslint-disable-next-line @typescript-eslint/naming-convention
							'.StripeElement': {
								width: '100%',
							},
							...(isSunriseEnabled && sunriseInputStyles),
						}}
					/>
				</Box>
				<Box sx={{ flex: 1 }} data-testid="credit-card-cvc">
					<Input
						className="iframe-form-element" // TODO: this is here for the regression-qaa tests. Migrate the tests to use the data-qa attribute.
						data-qa="iframe-form-element"
						error={validationErrors?.card_cvc}
						label={
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
							(
								<Box sx={{ height: 20 }}>
									<Translate value="corgi.payments.stripe_elements.cvc" />{' '}
									<Box
										sx={{
											display: 'inline-block',
											verticalAlign: 'middle',
											borderRadius: '50%',
											// eslint-disable-next-line @typescript-eslint/naming-convention
											':focus-within': {
												outlineWidth: '1px',
												outlineColor: 'secondary-blue',
												outlineStyle: 'solid',
											},
										}}
									>
										<InfoTooltip
											data-testid="credit-card-cvc-info"
											tabIndex={0}
											id="cvc-tooltip"
											isSunriseEnabled={isSunriseEnabled}
										>
											<Text>{translate('corgi.payments.stripe_elements.cvc_tooltip')}</Text>
										</InfoTooltip>
									</Box>
								</Box>
							) as unknown as string
						}
						name="card-cvc"
						as={CardCvcElementWrapper}
						onFocus={onCardCVCFieldFocus}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						onChange={onChange as unknown as ChangeEventHandler<HTMLInputElement>}
						id="card-cvc-input"
						isSunriseEnabled={isSunriseEnabled}
						sx={{
							height: 40,
							display: 'flex',
							alignItems: 'center',
							// eslint-disable-next-line @typescript-eslint/naming-convention
							'.StripeElement': {
								width: '100%',
							},
							...(isSunriseEnabled && sunriseInputStyles),
						}}
					/>
				</Box>
			</Flex>
		</>
	);
}
